"use client";

import { useInitialContext } from "@repo/sdk/provider";
import { STATSIG_EVENTS } from "@repo/statsig";
import { useStatsigClient } from "@repo/statsig";
import { useRouter } from "next/navigation";
import { useEffect } from "react";
import {
  checkForActiveVisit,
  confirmVisitRequest,
} from "src/app/select-patient/actions";
import { KinsaInitialContext, Patient } from "src/lib/definitions";
import { Routes } from "src/lib/routes";
import { useButtonPendingAction } from "src/lib/use-button-pending-action";
import { extractAvatarVariant } from "src/lib/utils/avatar-utils";
import { useSubscriptionCheck } from "src/lib/subscription-gate";
import type { SubscriptionState } from "src/lib/subscription-gate";
import KinsaVisitListEmptyPure, {
  type EmptyStateVariant,
} from "../visit-list-empty-pure";
import SubscriptionBanner from "../subscription-banner";
import PaymentProcessingIssueState from "./payment-processing-issue";
import SubscriptionCancelledState from "./subscription-cancelled";

interface VisitListEmptyProps {
  /** When provided (wizard context), dispatches START_VISIT through the gate system. */
  onStartVisit?: () => void;
  /** When provided (wizard context), dispatches SHOW_PAYWALL to show paywall in-wizard. */
  onExplorePlans?: () => void;
  /** Server-computed subscription status — used as fallback before SWR resolves. */
  hasSubscription?: boolean;
  className?: string;
}

/** Subscription states that map a paid user to a non-"active" empty-state variant. */
const PAID_VARIANT: Partial<Record<SubscriptionState, EmptyStateVariant>> = {
  cancelled: "cancelled",
  payment_issue: "payment_issue",
};

const VisitListEmpty: React.FC<VisitListEmptyProps> = ({
  onStartVisit,
  onExplorePlans,
  hasSubscription,
  className,
}) => {
  const { subscriptionState } = useSubscriptionCheck();
  const initialContext = useInitialContext<KinsaInitialContext>();
  const coveredPersons = initialContext?.account?.coveredPersons;
  const npatients = coveredPersons?.length ?? 0;
  const router = useRouter();
  const { isPending, startAction } = useButtonPendingAction();
  const { logEvent } = useStatsigClient();

  // `hasSubscription` is the authoritative server signal (paymentId on the
  // account, or a just-completed checkout). A user with no paymentId has never
  // subscribed — show onboarding regardless of any stale/loading client SWR
  // value (the `subscription-status` cache can hold a stale "active" from an
  // earlier optimistic update and would otherwise upgrade them incorrectly).
  // For users we know have paid, `subscriptionState` refines the variant.
  // States not in the map ("active", "canceling", "unknown", or a
  // loading/undefined value) fall through to "active":
  //  - "unknown" with a paymentId means checkout completed but the webhook
  //    hasn't created the subscription yet.
  //  - "canceling" still has access until period end — the banner in
  //    visit-list-pure communicates the cancellation.
  const effectiveState: EmptyStateVariant = !hasSubscription
    ? "never-subscribed"
    : (subscriptionState && PAID_VARIANT[subscriptionState]) || "active";

  // Log when the empty state page loads
  useEffect(() => {
    logEvent({
      eventName: "home_empty_state_viewed",
      value: effectiveState ?? "unknown",
      metadata: {
        subscription_state: effectiveState ?? "unknown",
        patient_count: npatients.toString(),
        will_show_patient_selector: (npatients > 1).toString(),
      },
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- fire once on mount

  // Get up to 3 avatar variants from covered persons
  const avatars = (coveredPersons ?? []).slice(0, 3).map((person) => ({
    variant: extractAvatarVariant(person.custom?.profileImage, person.id),
    id: person.id,
  }));

  const navigateToBlockVisit = (patient: Patient) => {
    const params = new URLSearchParams({
      id: patient.id,
      firstName: patient.firstName,
      ...(patient.custom?.profileImage && {
        profileImage: patient.custom.profileImage,
      }),
    });
    router.push(`${Routes.BLOCK_VISIT}?${params.toString()}`);
  };

  const handleGetCareNow = () => {
    startAction(async () => {
      if (npatients === 1) {
        const hasActiveVisit = await checkForActiveVisit(coveredPersons![0]);
        if (hasActiveVisit) {
          navigateToBlockVisit(coveredPersons![0]);
          return;
        }
        await confirmVisitRequest(coveredPersons![0]);
      } else {
        router.push(Routes.SELECT_PATIENT);
      }
    });
  };

  // Show subscription issue states when applicable
  if (effectiveState === "cancelled") {
    return <SubscriptionCancelledState className={className} />;
  }

  if (effectiveState === "payment_issue") {
    return <PaymentProcessingIssueState className={className} />;
  }

  const showCancelingBanner = subscriptionState === "canceling";

  return (
    <div className={`flex flex-col gap-4 ${className ?? ""}`}>
      {showCancelingBanner && <SubscriptionBanner />}
      <KinsaVisitListEmptyPure
        effectiveState={effectiveState}
        avatars={avatars}
        onGetCareNow={onStartVisit ?? handleGetCareNow}
        onPaywall={() => router.push(Routes.PAYWALL)}
        onValueProps={onExplorePlans ?? (() => router.push(Routes.PAYWALL))}
        isPending={isPending}
        patientCount={npatients}
      />
    </div>
  );
};

export default VisitListEmpty;
